import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivitiesGateway } from '@wilson/api/gateway';
import {
  ActivityWithCategoryAndLocationsAndReportedDetails,
  Shift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { ActivityResizeInformation } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DEFAULT_ACTIVITY_SNAPPING_MINUTES,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineState,
  TimelineUpdateShiftV2,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { differenceInMilliseconds } from 'date-fns';
import {
  Subscription,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
} from 'rxjs';
import { ActivityDropZoneHelperService } from '../components/activity-drop-zone/activity-drop-zone-helper.service';
import { ResizeActivityHelperService } from '../components/timeline-auto-size-shift-with-activities-card/TimelineAutoSizeActivityCard/resize-activity-helper.service';
import { DragAndDropHelperService } from './drag-and-drop-helper.service';
import {
  MouseMovementHelperService,
  SnapInformation,
} from './mouse-movement-helper.service';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';

const MINIMUM_ACTIVITY_WIDTH = 20;

@Injectable()
export class GanttActivityResizeHandlerHelperService implements OnDestroy {
  private readonly mouseMovementHelperService = inject(
    MouseMovementHelperService,
  );
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly activitiesService = inject(ActivitiesGateway);
  private readonly resizeActivityHelperService = inject(
    ResizeActivityHelperService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  private currentOffsetX!: number;
  private offsetXSubscription!: Subscription;
  private snappingInformation: SnapInformation[] = [];
  private subscription!: Subscription;

  setSnapping(activityData: ActivityResizeInformation): void {
    this.subscription = this.dragAndDropModeService.isSnapping$.subscribe(
      async (isSnapping) => {
        if (isSnapping) {
          this.snappingInformation =
            await this.resizeActivityHelperService.generateSnappingInformation(
              activityData.shiftActivities,
              activityData.activity.id || null,
            );
        }
      },
    );
  }

  onMouseDown(
    activityData: ActivityResizeInformation,
    isRightDirection: boolean,
  ): void {
    if (isRightDirection) {
      this.dragAndDropModeService.setCustomSnappingStepSize(
        DEFAULT_ACTIVITY_SNAPPING_MINUTES,
      );
    }
    this.mouseMovementHelperService.setUserDraggingActivity(true);
    this.mouseMovementHelperService.setMouseOffsetX(null);
    this.activityDropZoneHelperService.setActivity(activityData.activity);

    const isUserDraggingActivity$ =
      this.mouseMovementHelperService.isUserDraggingActivity$;
    const offsetX$ = this.mouseMovementHelperService.offsetX$.pipe(
      distinctUntilChanged(),
    );
    this.offsetXSubscription = combineLatest([
      isUserDraggingActivity$,
      offsetX$,
    ]).subscribe(async ([isUserDraggingActivity, offsetX]) => {
      const frameLength = this.store.selectSnapshot(
        ShiftTimelineState.frameLength,
      );
      if (isUserDraggingActivity && offsetX !== null) {
        const shiftToUpdateId = activityData.activity.shiftId;

        const actualSnapInfo =
          this.resizeActivityHelperService.getActualSnapInfo(
            offsetX,
            this.snappingInformation,
          );
        const actualSnapPx = actualSnapInfo.actualSnapPx;

        this.currentOffsetX =
          actualSnapPx + DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL;

        let dateToUpdate = this.dragAndDropHelperService.calculateTimeFromPx(
          this.currentOffsetX,
          DEFAULT_ACTIVITY_SNAPPING_MINUTES,
        );
        if (actualSnapInfo.snappedDate) {
          dateToUpdate = actualSnapInfo.snappedDate;
        }
        if (isRightDirection) {
          this.handlePreviewForRightResize(
            activityData,
            this.currentOffsetX,
            shiftToUpdateId,
            dateToUpdate,
            frameLength,
          );
        } else {
          this.handlePreviewForLeftResize(
            activityData,
            this.currentOffsetX,
            shiftToUpdateId,
            dateToUpdate,
            frameLength,
          );
        }
      } else {
        if (!isUserDraggingActivity) {
          this.offsetXSubscription?.unsubscribe();
          if (isRightDirection) {
            await this.handleDropRight(activityData);
          } else {
            await this.handleDropLeft(activityData);
          }
        }
      }
    });
  }

  private handlePreviewForLeftResize(
    activityData: ActivityResizeInformation,
    offsetX: number,
    shiftId: string | null,
    dateToUpdate: Date,
    frameLength: number,
  ): void {
    if (
      activityData.activity &&
      shiftId &&
      activityData.userId &&
      activityData.rightPx
    ) {
      const newDuration = differenceInMilliseconds(
        new Date(activityData.activity.endDatetime),
        dateToUpdate,
      );
      const activityWidth = frameLength - activityData.rightPx - offsetX;
      if (activityWidth >= MINIMUM_ACTIVITY_WIDTH) {
        this.store.dispatch(
          new TimelineUpsertActivityV2(
            [
              {
                ...activityData.activity,
                startDatetime: dateToUpdate.toISOString(),
              },
            ],
            shiftId,
          ),
        );
        this.resizeActivityHelperService.updateDemarcatorLine({
          left: offsetX,
          right: activityData.rightPx,
          shiftDuration: newDuration,
          adjustmentNeeded: false,
        });
        this.handleShiftResizeLeft(shiftId, activityData.userId);
        this.dragAndDropModeService.setDraggingItemStartDatetime(dateToUpdate);
        this.dragAndDropModeService.setDraggingItemEndDatetime(
          new Date(activityData.activity.endDatetime),
        );
      }
    }
  }

  private handlePreviewForRightResize(
    activityData: ActivityResizeInformation,
    offsetX: number,
    shiftId: string | null,
    dateToUpdate: Date,
    frameLength: number,
  ): void {
    if (
      activityData.activity &&
      shiftId &&
      activityData.userId &&
      activityData.leftPx
    ) {
      const newDuration = differenceInMilliseconds(
        dateToUpdate,
        new Date(activityData.activity.startDatetime),
      );
      const actualRightPx = frameLength - offsetX;
      const activityWidth = frameLength - actualRightPx - activityData.leftPx;
      if (activityWidth >= MINIMUM_ACTIVITY_WIDTH) {
        this.store.dispatch(
          new TimelineUpsertActivityV2(
            [
              {
                ...activityData.activity,
                endDatetime: dateToUpdate.toISOString(),
              },
            ],
            shiftId,
          ),
        );
        this.resizeActivityHelperService.updateDemarcatorLine({
          left: activityData.leftPx,
          right: actualRightPx,
          shiftDuration: newDuration,
          adjustmentNeeded: false,
        });
        this.handleShiftResizeRight(shiftId, activityData.userId);
        this.dragAndDropModeService.setDraggingItemStartDatetime(
          new Date(activityData.activity.startDatetime),
        );
        this.dragAndDropModeService.setDraggingItemEndDatetime(dateToUpdate);
      }
    }
  }

  private handleShiftResizeLeft(shiftId: string, userId: string): void {
    const selectedShift = this.store.selectSnapshot(
      ShiftTimelineDataState.getShiftOfUser(shiftId, userId),
    );
    if (selectedShift) {
      const allActivities = this.getShiftActivities(selectedShift);
      allActivities.sort((a, b) =>
        a.startDatetime.localeCompare(b.startDatetime),
      );
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...selectedShift,
          startDatetime: allActivities[0].startDatetime,
        }),
      );
    }
  }

  private handleShiftResizeRight(shiftId: string, userId: string): void {
    const selectedShift = this.store.selectSnapshot(
      ShiftTimelineDataState.getShiftOfUser(shiftId, userId),
    );
    if (selectedShift) {
      const allActivities = this.getShiftActivities(selectedShift);
      allActivities.sort((a, b) => a.endDatetime.localeCompare(b.endDatetime));
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...selectedShift,
          endDatetime: allActivities[allActivities.length - 1].endDatetime,
        }),
      );
    }
  }

  private getShiftActivities(
    selectedShift: Shift &
      WithPreparedAttributes & {
        id: string;
      },
  ): ActivityWithCategoryAndLocationsAndReportedDetails[] {
    const activitiesInRow = this.store.selectSnapshot(
      ShiftTimelineDataState.shiftDeterminedActivities(selectedShift.id),
    );

    return activitiesInRow.flatMap((row) => Object.values(row));
  }

  private async handleDropLeft(
    activityData: ActivityResizeInformation,
  ): Promise<void> {
    const activityFromState = this.store.selectSnapshot(
      ShiftTimelineDataState.activityOfShift({
        shiftId: activityData.activity.shiftId || '',
        activityId: activityData.activity.id || '',
      }),
    );

    if (
      activityFromState?.startDatetime &&
      activityData.userId &&
      activityData.activity.shiftId &&
      activityData.shiftPublicationStatus
    ) {
      const dateToUpdate = new Date(activityFromState.startDatetime);
      const { activityIndex, isActivityWithinShiftBounds } =
        this.resizeActivityHelperService.determineActivityDetails(
          dateToUpdate,
          activityData.shiftActivities,
          activityData.activity,
          activityData.accurateShiftStartDateTime,
          activityData.accurateShiftEndDateTime,
        );
      this.resizeActivityHelperService.resetDemarcatorLine();

      if (activityIndex === 0 || !isActivityWithinShiftBounds) {
        const userConfirmedPublishedShiftEdit =
          await this.timelineCommonOperationsService.checkAndConfirmPublishedShiftDrop(
            activityData.shiftPublicationStatus,
          );
        if (userConfirmedPublishedShiftEdit) {
          await this.resizeActivityHelperService.updateLeftHandleActivity(
            activityData.activity,
            activityData.userId,
            dateToUpdate,
          );
        } else {
          this.store.dispatch(
            new TimelineUpsertActivityV2(
              [activityData.activity],
              activityData.activity.shiftId as string,
            ),
          );
        }
      } else {
        await this.resizeActivityHelperService.updateLeftHandleActivity(
          activityData.activity,
          activityData.userId,
          dateToUpdate,
        );
      }
      await this.timelineCommonOperationsService.updateUsersAssignmentsState([
        activityData.userId,
      ]);
    }
  }

  private async handleDropRight(
    activityData: ActivityResizeInformation,
  ): Promise<void> {
    const activityFromState = this.store.selectSnapshot(
      ShiftTimelineDataState.activityOfShift({
        shiftId: activityData.activity.shiftId || '',
        activityId: activityData.activity.id || '',
      }),
    );

    if (
      activityFromState?.endDatetime &&
      activityData.userId &&
      activityData.activity.shiftId &&
      activityData.shiftPublicationStatus
    ) {
      const dateToUpdate = new Date(activityFromState.endDatetime);
      const { activityIndex, isActivityWithinShiftBounds } =
        this.resizeActivityHelperService.determineActivityDetails(
          dateToUpdate,
          activityData.shiftActivities,
          activityData.activity,
          activityData.accurateShiftStartDateTime,
          activityData.accurateShiftEndDateTime,
        );

      this.resizeActivityHelperService.resetDemarcatorLine();
      if (
        activityIndex === activityData.shiftActivities.length - 1 ||
        !isActivityWithinShiftBounds
      ) {
        const userConfirmedPublishedShiftEdit =
          await this.timelineCommonOperationsService.checkAndConfirmPublishedShiftDrop(
            activityData.shiftPublicationStatus,
          );
        if (userConfirmedPublishedShiftEdit) {
          await this.resizeActivityHelperService.updateRightHandleActivity(
            activityData.activity,
            activityData.userId,
            dateToUpdate,
          );
        } else {
          this.store.dispatch(
            new TimelineUpsertActivityV2(
              [activityData.activity],
              activityData.activity.shiftId,
            ),
          );
        }
      } else {
        await this.resizeActivityHelperService.updateRightHandleActivity(
          activityData.activity,
          activityData.userId,
          dateToUpdate,
        );
      }
      await this.timelineCommonOperationsService.updateUsersAssignmentsState([
        activityData.userId,
      ]);
    }
  }

  async fillActivityGapLeft(
    activityData: ActivityResizeInformation,
  ): Promise<void> {
    if (activityData.activity.shiftId && activityData.userId) {
      try {
        const snappingStepSize = await firstValueFrom(
          this.dragAndDropModeService.snappingStepSize$,
        );
        const activityIndex = activityData.shiftActivities.findIndex(
          (activity) => activity.id === activityData.activity.id,
        );

        if (activityIndex > 0) {
          this.store.dispatch(
            new TimelineUpsertActivityV2(
              [
                {
                  ...activityData.activity,
                  startDatetime:
                    activityData.shiftActivities[activityIndex - 1].endDatetime,
                },
              ],
              activityData.activity.shiftId,
            ),
          );
          this.resizeActivityHelperService.showGapFillAnimation(
            activityData.shiftActivities[activityIndex - 1].endDatetime,
            snappingStepSize,
            activityData.userId,
          );
          await this.activitiesService.updateActivity({
            id: activityData.activity.id,
            startDatetime:
              activityData.shiftActivities[activityIndex - 1].endDatetime,
          });
        }
      } catch {
        this.store.dispatch(
          new TimelineUpsertActivityV2(
            [activityData.activity],
            activityData.activity.shiftId,
          ),
        );
        this.mouseMovementHelperService.setAnimationOffsetX(null);
        this.activityDropZoneHelperService.setUserId(null);
      }
    }
  }

  async fillActivityGapRight(
    activityData: ActivityResizeInformation,
  ): Promise<void> {
    if (activityData.activity.shiftId && activityData.userId) {
      try {
        const snappingStepSize = await firstValueFrom(
          this.dragAndDropModeService.snappingStepSize$,
        );
        const activityIndex = activityData.shiftActivities.findIndex(
          (activity) => activity.id === activityData.activity.id,
        );

        if (activityIndex < activityData.shiftActivities.length - 1) {
          this.store.dispatch(
            new TimelineUpsertActivityV2(
              [
                {
                  ...activityData.activity,
                  endDatetime:
                    activityData.shiftActivities[activityIndex + 1]
                      .startDatetime,
                },
              ],
              activityData.activity.shiftId,
            ),
          );
          this.resizeActivityHelperService.showGapFillAnimation(
            activityData.shiftActivities[activityIndex + 1].startDatetime,
            snappingStepSize,
            activityData.userId,
          );
          await this.activitiesService.updateActivity({
            id: activityData.activity.id,
            endDatetime:
              activityData.shiftActivities[activityIndex + 1].startDatetime,
          });
        }
      } catch {
        this.store.dispatch(
          new TimelineUpsertActivityV2(
            [activityData.activity],
            activityData.activity.shiftId,
          ),
        );
        this.mouseMovementHelperService.setAnimationOffsetX(null);
        this.activityDropZoneHelperService.setUserId(null);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
